import type {
    TransportOrderDetail,
    TransportOrderMilestone,
    TransportOrderMilestoneType,
} from 'api/generated/transportOrdersApi';
import { formatCoordinates } from 'utils/formatCoordinates';
import { formatDate, formatTime } from 'utils/formatDateTime';

import TransportOrderDetailMilestoneItem from './TransportOrderDetailMilestoneItem';
import styles from './TransportOrderDetailMilestones.module.scss';

type MilestoneAppearance = { title: string; badgeColor: string; iconName: string };

const MILESTONE_APPEARANCE: Record<TransportOrderMilestoneType, MilestoneAppearance> = {
    DISPATCHED: {
        title: 'Dispatched',
        badgeColor: 'badge-success',
        iconName: 'icon-ok',
    },
    STOPPED: {
        title: 'Stopped',
        badgeColor: 'badge-danger',
        iconName: 'icon-warning-sign',
    },
    DELIVERED: {
        title: 'Delivered',
        badgeColor: 'badge-success',
        iconName: 'icon-stopover',
    },
};

const LATE_DELIVERY: MilestoneAppearance = {
    title: 'Delivered',
    badgeColor: 'badge-warning',
    iconName: 'icon-exclamation-sign',
};

const PENDING_DELIVERY: MilestoneAppearance = {
    title: 'Delivery',
    badgeColor: 'badge-default',
    iconName: 'icon-stopover',
};

const milestoneAppearance = (milestone: TransportOrderMilestone, order: TransportOrderDetail) =>
    milestone.type === 'DELIVERED' && order.deliveryStatus === 'DELAYED'
        ? LATE_DELIVERY
        : MILESTONE_APPEARANCE[milestone.type];

const formatDetail = (occurredAt: string | undefined, place: string | undefined) => {
    const when = occurredAt === undefined ? ['No ETA'] : [formatTime(occurredAt), formatDate(occurredAt)];

    return [...when, place].filter(part => part !== undefined && part !== '').join(' • ');
};

const milestonePlace = (milestone: TransportOrderMilestone, order: TransportOrderDetail) => {
    switch (milestone.type) {
        case 'DISPATCHED':
            return order.launchHub.name;
        case 'DELIVERED':
            return order.landingHub.name;
        default:
            return milestone.position === undefined ? undefined : formatCoordinates(milestone.position);
    }
};

export type TransportOrderDetailMilestonesProps = {
    order: TransportOrderDetail;
};

const TransportOrderDetailMilestones = ({ order }: TransportOrderDetailMilestonesProps) => {
    const { milestones } = order;

    if (milestones.length === 0) {
        return null;
    }

    const isDelivered = milestones.some(milestone => milestone.type === 'DELIVERED');

    return (
        <div className={`timeline margin-top-5 ${styles.disableLastLine}`}>
            {milestones.map(milestone => {
                const { title, badgeColor, iconName } = milestoneAppearance(milestone, order);
                const detail = formatDetail(milestone.occurredAt, milestonePlace(milestone, order));

                return (
                    <TransportOrderDetailMilestoneItem
                        key={`${milestone.type}-${detail}`}
                        title={title}
                        detail={detail}
                        badgeColor={badgeColor}
                        iconName={iconName}
                    />
                );
            })}
            {isDelivered ? null : (
                <TransportOrderDetailMilestoneItem
                    title={PENDING_DELIVERY.title}
                    detail={formatDetail(order.progressDetails?.eta, order.landingHub.name)}
                    badgeColor={PENDING_DELIVERY.badgeColor}
                    iconName={PENDING_DELIVERY.iconName}
                />
            )}
        </div>
    );
};

export default TransportOrderDetailMilestones;
